/**
 * Broadcast / communications store (ADR 0053). Same house shape as
 * emailTemplateStore: zustand+immer, `(getToken) => async (...)` curried
 * thunks, Bearer header per call. Send is async on the API side — POST
 * /broadcasts/d/:id/send returns 202 {jobId} (or 503 with a user-appropriate
 * message when the worker is down); callers poll fetchBroadcast(s) while a
 * broadcast is queued/sending.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from './axios';
import { showError } from '../actions/globalActions';
import getApiErrorMessage from '../utils/getApiErrorMessage';

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token: string) => ({
  headers: {
    'Authorization': `Bearer ${token}`,
  },
});

export type BroadcastScope = 'organization' | 'registration' | 'contest' | 'division';
export type BroadcastAudience = 'managers' | 'everyone';
export type BroadcastStatus = 'draft' | 'queued' | 'sending' | 'sent' | 'failed';

export interface BroadcastSegment {
  scope: BroadcastScope;
  targetId?: string;
  audience: BroadcastAudience;
}

export interface Broadcast {
  _id: string;
  organization?: string;
  segment: BroadcastSegment;
  subject: string;
  body: string;
  status: BroadcastStatus;
  recipientCount?: number;
  sentCount?: number;
  failedCount?: number;
  sentAt?: string;
  created?: string;
  createdBy?: string;
}

export interface AudiencePreview {
  recipientCount: number;
  sample: string[];
}

export interface PreviewAudiencePayload {
  organization: string;
  segment: BroadcastSegment;
}

export interface CreateBroadcastPayload extends PreviewAudiencePayload {
  subject: string;
  body: string;
  // 'html' = Quill-authored HTML (compose page); omitted = legacy plain text.
  bodyFormat?: 'text' | 'html';
}

type GetToken = () => Promise<string | null>;

interface BroadcastState {
  broadcasts: Broadcast[];
  fetchBroadcasts: (getToken: GetToken) => (organizationId: string) => Promise<Broadcast[]>;
  previewAudience: (getToken: GetToken) => (payload: PreviewAudiencePayload) => Promise<AudiencePreview | null>;
  createBroadcast: (getToken: GetToken) => (payload: CreateBroadcastPayload) => Promise<Broadcast>;
  sendBroadcast: (getToken: GetToken) => (id: string) => Promise<{ jobId: string; queue: string }>;
  fetchBroadcast: (getToken: GetToken) => (id: string) => Promise<Broadcast | null>;
}

const useBroadcastStore = create<BroadcastState>()(immer((set) => ({
  broadcasts: [],

  fetchBroadcasts: (getToken: GetToken) => async (organizationId: string) => {
    try {
      const token = await getToken();
      const { data } = await axios.get(
        `${baseUrl}/broadcasts/d?organization=${organizationId}`,
        createAuthHeader(token as string),
      );
      const result: Broadcast[] = data.result || [];
      set((state) => {
        state.broadcasts = result;
      });
      return result;
    } catch (error) {
      console.error('Error in fetchBroadcasts:', error);
      return [];
    }
  },

  // Live-count preview while composing — quiet on failure (it fires on every
  // debounced segment change; the compose page shows "unavailable" instead).
  previewAudience: (getToken: GetToken) => async (payload: PreviewAudiencePayload) => {
    try {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/broadcasts/d/preview`,
        payload,
        createAuthHeader(token as string),
      );
      return (data.result || null) as AudiencePreview | null;
    } catch (error) {
      console.error('Error in previewAudience:', error);
      return null;
    }
  },

  createBroadcast: (getToken: GetToken) => async (payload: CreateBroadcastPayload) => {
    try {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/broadcasts/d`,
        payload,
        createAuthHeader(token as string),
      );
      const result: Broadcast = data.result;
      set((state) => {
        state.broadcasts.unshift(result);
      });
      return result;
    } catch (error: any) {
      if (!error?.permissionToastShown) {
        showError('Error', getApiErrorMessage(error, 'Failed to save the message'));
      }
      console.error('Error in createBroadcast:', error);
      throw error;
    }
  },

  // 202 {jobId, queue} on success; 503 when the worker is down — the server's
  // message is user-appropriate, so surface it verbatim.
  sendBroadcast: (getToken: GetToken) => async (id: string) => {
    try {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/broadcasts/d/${id}/send`,
        {},
        createAuthHeader(token as string),
      );
      set((state) => {
        const index = state.broadcasts.findIndex((b) => b._id === id);
        if (index >= 0) state.broadcasts[index].status = 'queued';
      });
      return data as { jobId: string; queue: string };
    } catch (error: any) {
      if (!error?.permissionToastShown) {
        showError('Error', getApiErrorMessage(error, 'Failed to send the message'));
      }
      console.error('Error in sendBroadcast:', error);
      throw error;
    }
  },

  fetchBroadcast: (getToken: GetToken) => async (id: string) => {
    try {
      const token = await getToken();
      const { data } = await axios.get(
        `${baseUrl}/broadcasts/d/${id}`,
        createAuthHeader(token as string),
      );
      const result: Broadcast | null = data.result || null;
      set((state) => {
        if (!result) return;
        const index = state.broadcasts.findIndex((b) => b._id === id);
        if (index >= 0) state.broadcasts[index] = result;
        else state.broadcasts.push(result);
      });
      return result;
    } catch (error) {
      console.error('Error in fetchBroadcast:', error);
      return null;
    }
  },
})));

export default useBroadcastStore;
